<template>
  <div>
    <c-title>Persönliche Daten</c-title>

    <div v-if="showImportHint">
      <strong>+ + + Daten werden gerade synchronisiert + + +</strong>
      <p>Vielen Dank für Ihre Registrierung! Ihre bereits in unserem System gespeicherten Daten werden aktuell
      synchronisiert. Dieser Vorgang dauert in der Regel 10 Minuten.</p>
      <p>Bitte rufen Sie diese Seite dann erneut auf und überprüfen Sie, ob alle Angaben korrekt und vollständig sind. Sie
      können Ihre Daten dann jederzeit an dieser Stelle aktualisieren.</p>

      <p>Bei Fragen wenden Sie sich bitte an uns: <a
        href="mailto:info@claussen-simon-stiftung.de"
      >info@claussen-simon-stiftung.de</a></p>
    </div>

    <c-busy v-if="loadStatus === $config.status.LOADING" />

    <c-form
      v-else-if="loadStatus === $config.status.SUCCESS"
      required-fields
      @submit.prevent="submit"
    >

      <c-form-fieldset>

        <c-form-list
          id="gender"
          :errors="[{
            message: 'Bitte wählen Sie Ihr Geschlecht.',
            show: !$v.gender.required
          }]"
          :has-error="$v.gender.$error"
          inline
          label="Geschlecht *"
        >

          <c-form-list-item>
            <c-form-radio
              id="gender-male"
              v-model="gender"
              label="Männlich"
              name="gender"
              value="Mann"
              @input="$v.gender.$touch"
            />
          </c-form-list-item>

          <c-form-list-item>
            <c-form-radio
              id="gender-female"
              v-model="gender"
              label="Weiblich"
              name="gender"
              value="Frau"
              @input="$v.gender.$touch"
            />
          </c-form-list-item>

          <c-form-list-item>
            <c-form-radio
              id="gender-diverse"
              v-model="gender"
              label="Divers"
              name="gender"
              value="Divers"
              @input="$v.gender.$touch"
            />
          </c-form-list-item>

        </c-form-list>

        <c-grid>

          <c-grid-item :span="2">
            <c-form-input
              id="title"
              v-model="title"
              aria-required="false"
              label="Titel"
            />
          </c-grid-item>

          <c-grid-item :span="5">
            <c-form-input
              id="first-name"
              :errors="[{
                message: 'Dieses Feld ist erforderlich.',
                show: !$v.firstName.required
              }]"
              :has-error="$v.firstName.$error"
              :is-valid="!$v.firstName.$invalid"
              v-model="firstName"
              aria-required="true"
              autocomplete="given-name"
              label="Vorname *"
              @input="$v.firstName.$touch"
            />
          </c-grid-item>

          <c-grid-item :span="5">
            <c-form-input
              id="last-name"
              :errors="[{
                message: 'Dieses Feld ist erforderlich.',
                show: !$v.lastName.required
              }]"
              :has-error="$v.lastName.$error"
              :is-valid="!$v.lastName.$invalid"
              v-model="lastName"
              aria-required="true"
              autocomplete="family-name"
              label="Nachname *"
              @input="$v.lastName.$touch"
            />
          </c-grid-item>

          <c-form-select
            id="pronouns"
            v-model="pronouns"
            aria-required="false"
            label="Pronomen"
          >

            <option
              :selected="!pronouns"
              value=""
            >
              –
            </option>

            <option
              v-for="pronoun in $store.state.user.data.pronoun_choices"
              :key="pronoun"
              :selected="pronoun === pronouns"
              :value="pronoun"
            >
              {{ pronoun }}
            </option>

          </c-form-select>

          <c-form-datepicker
            id="date-of-birth"
            :errors="[{
              message: 'Dieses Feld ist erforderlich.',
              show: !$v.dateOfBirth.required
            }]"
            :has-error="$v.dateOfBirth.$error"
            :is-valid="!$v.dateOfBirth.$invalid"
            :max-date="new Date()"
            v-model="dateOfBirth"
            autocomplete="bday"
            label="Geburtsdatum *"
            @input="$v.dateOfBirth.$touch"
          />

          <c-form-input
            id="country-of-birth"
            :errors="[{
              message: 'Dieses Feld ist erforderlich.',
              show: !$v.countryOfBirth.required
            }]"
            :has-error="$v.countryOfBirth.$error"
            :is-valid="!$v.countryOfBirth.$invalid"
            v-model="countryOfBirth"
            aria-required="true"
            label="Geburtsland *"
            @input="$v.countryOfBirth.$touch"
          />

        </c-grid>

      </c-form-fieldset>

      <c-form-fieldset legend="Anschrift">

        <c-grid>

          <c-grid-item :span="8">
            <c-form-input
              id="street"
              :errors="[{
                message: 'Dieses Feld ist erforderlich.',
                show: !$v.street.required
              }]"
              :has-error="$v.street.$error"
              :is-valid="!$v.street.$invalid"
              v-model="street"
              aria-required="true"
              autocomplete="street-address"
              label="Straße *"
              @input="$v.street.$touch"
            />
          </c-grid-item>

          <c-grid-item :span="4">
            <c-form-input
              id="house-number"
              :errors="[{
                message: 'Dieses Feld ist erforderlich.',
                show: !$v.houseNumber.required
              }, {
                message: 'Bitte geben Sie eine gültige Hausnummer ein.',
                show: $v.postalCode.maxLength
              }]"
              :has-error="$v.houseNumber.$error"
              :is-valid="!$v.houseNumber.$invalid"
              v-model="houseNumber"
              aria-required="true"
              label="Hausnummer *"
              @input="$v.houseNumber.$touch"
            />
          </c-grid-item>

          <c-grid-item :span="4">
            <c-form-input
              id="postal-code"
              :errors="[{
                message: 'Dieses Feld ist erforderlich.',
                show: !$v.postalCode.required
              }, {
                message: 'Bitte geben Sie eine gültige Postleitzahl ein.',
                show: !$v.postalCode.maxLength || !$v.postalCode.minLength
              }]"
              :has-error="$v.postalCode.$error"
              :is-valid="!$v.postalCode.$invalid"
              v-model="postalCode"
              aria-required="true"
              autocomplete="postal-code"
              label="Postleitzahl *"
              @input="$v.postalCode.$touch"
            />
          </c-grid-item>

          <c-grid-item :span="8">
            <c-form-input
              id="city"
              :errors="[{
                message: 'Dieses Feld ist erforderlich.',
                show: !$v.city.required
              }]"
              :has-error="$v.city.$error"
              :is-valid="!$v.city.$invalid"
              v-model="city"
              aria-required="true"
              autocomplete="address-level2"
              label="Ort *"
              @input="$v.city.$touch"
            />
          </c-grid-item>

          <c-grid-item :span="4">
            <c-form-select
              id="state"
              :errors="[{
                message: 'Dieses Feld ist erforderlich.',
                show: !$v.state.required
              }]"
              :has-error="$v.state.$error"
              :is-valid="!$v.state.$invalid"
              v-model="state"
              aria-required="true"
              label="Bundesland *"
              @input="$v.state.$touch"
            >

              <option
                disabled
                selected
                value=""
              >
                Bitte wählen
              </option>

              <option value="Baden-Württemberg">Baden-Württemberg</option>
              <option value="Bayern">Bayern</option>
              <option value="Berlin">Berlin</option>
              <option value="Brandenburg">Brandenburg</option>
              <option value="Bremen">Bremen</option>
              <option value="Hamburg">Hamburg</option>
              <option value="Hessen">Hessen</option>
              <option value="Mecklenburg-Vorpommern">Mecklenburg-Vorpommern</option>
              <option value="Niedersachsen">Niedersachsen</option>
              <option value="Nordrhein-Westfalen">Nordrhein-Westfalen</option>
              <option value="Rheinland-Pfalz">Rheinland-Pfalz</option>
              <option value="Saarland">Saarland</option>
              <option value="Sachsen">Sachsen</option>
              <option value="Sachsen-Anhalt">Sachsen-Anhalt</option>
              <option value="Schleswig-Holstein">Schleswig-Holstein</option>
              <option value="Thüringen">Thüringen</option>

            </c-form-select>
          </c-grid-item>

        </c-grid>

      </c-form-fieldset>

      <c-form-fieldset legend="Kontakt">

        <c-form-input
          id="phone"
          :errors="[{
            message: 'Dieses Feld ist erforderlich.',
            show: !$v.phone.required
          }]"
          :has-error="$v.phone.$error"
          :is-valid="!$v.phone.$invalid"
          v-model="phone"
          class="input__marginless"
          aria-required="true"
          autocomplete="tel"
          label="Telefonnummer *"
          pattern="\+[0-9]+"
          type="tel"
          @input="$v.phone.$touch"
        />

        <i>Format: +49123456789</i>

      </c-form-fieldset>

      <c-form-fieldset legend="Weitere Informationen">

        <c-grid>
          <c-grid-item :span="2">
            <img
              v-if="image"
              :alt="`Foto von ${firstName} ${lastName}.`"
              :src="tempImage"
              width="150"
              height="150"
            >
            <p v-else>Kein Foto hinterlegt.</p>
          </c-grid-item>

          <c-grid-item :span="10">
            <c-form-input
              id="image"
              accept=".jpg,.jpeg,.png"
              label="Foto (JPEG, PNG)"
              type="file"
              @change="changeImage($event[0])"
            />
          </c-grid-item>

        </c-grid>

        <c-form-select
          id="regio-group"
          :errors="[{
            message: 'Dieses Feld ist erforderlich.',
            show: !$v.regionalGroup.required
          }]"
          :has-error="$v.regionalGroup.$error"
          :is-valid="!$v.regionalGroup.$invalid"
          v-model.number="regionalGroup"
          aria-required="true"
          label="Regionalgruppe *"
          help="In den verschiedenen Regionen Deutschlands haben sich Regionalgruppen unserer aktiven und ehemaligen Geförderten zusammengefunden, die regelmäßig Treffen organisieren und etwas unternehmen."
          @input="$v.regionalGroup.$touch"
        >

          <option
            disabled
            selected
            value=""
          >
            Bitte wählen Sie eine Regionalgruppe
          </option>

          <option
            v-for="group in $store.state.user.data.regional_groups"
            :key="group.id"
            :selected="group.id === regionalGroup"
            :value="group.id"
          >
            {{ group.name }}
          </option>

        </c-form-select>

        <c-form-list
          id="consent"
          :errors="[{
            message: 'Bitte stimmen Sie allen Anforderungen zu.',
            show: !$v.dataEvaluation.is || !$v.dataThirdParty.is || !$v.dataProtection.is
          }]"
          :has-error="$v.dataEvaluation.$error || $v.dataThirdParty.$error || $v.dataProtection.$error"
        >

          <c-form-list-item>
            <c-form-checkbox
              id="data-evaluation"
              :has-error="$v.dataEvaluation.$error"
              v-model="dataEvaluation"
              aria-required="true"
              label="Ich bin damit einverstanden, dass meine Daten im Rahmen des Auswahlverfahrens gespeichert und verarbeitet werden."
              @change="$v.dataEvaluation.$touch"
            />
          </c-form-list-item>

          <c-form-list-item>
            <c-form-checkbox
              id="data-third-party"
              :has-error="$v.dataThirdParty.$error"
              v-model="dataThirdParty"
              aria-required="true"
              label="Ich bin damit einverstanden, dass meine Daten nach meiner Aufnahme in das Stipendienprogramm an Dritte übermittelt werden."
              @change="$v.dataThirdParty.$touch"
            />
          </c-form-list-item>

          <c-form-list-item>
            <c-form-checkbox
              id="data-protection"
              :has-error="$v.dataProtection.$error"
              v-model="dataProtection"
              aria-required="true"
              @change="$v.dataProtection.$touch"
            >
              Ich habe die <a
                href="https://www.claussen-simon-stiftung.de/de/stiftung/impressum/"
                target="_blank"
              >Datenschutzhinweise</a> gelesen und akzeptiere diese.
            </c-form-checkbox>
          </c-form-list-item>

        </c-form-list>

      </c-form-fieldset>

      <c-button :busy="saveStatus === $config.status.SAVING">
        Speichern
      </c-button>

    </c-form>
  </div>
</template>

<script>
import CBusy from '@/components/CBusy'
import CButton from '@/components/CButton'
import CForm from '@/components/form/CForm'
import CFormCheckbox from '@/components/form/CFormCheckbox'
import CFormDatepicker from '@/components/form/CFormDatepicker'
import CFormFieldset from '@/components/form/CFormFieldset'
import CFormInput from '@/components/form/CFormInput'
import CFormList from '@/components/form/CFormList'
import CFormListItem from '@/components/form/CFormListItem'
import CFormRadio from '@/components/form/CFormRadio'
import CFormSelect from '@/components/form/CFormSelect'
import CGrid from '@/components/CGrid'
import CGridItem from '@/components/CGridItem'
import CTitle from '@/components/CTitle'

import handleRequestError from '@/functions/handleRequestError'
import { maxLength, minLength, required } from 'vuelidate/lib/validators'
import { is } from '@/functions/validators'

export default {
  name: 'VDashboardPersonalData',

  components: {
    CBusy,
    CButton,
    CForm,
    CFormCheckbox,
    CFormDatepicker,
    CFormFieldset,
    CFormInput,
    CFormList,
    CFormListItem,
    CFormRadio,
    CFormSelect,
    CGrid,
    CGridItem,
    CTitle
  },

  data () {
    return {
      loadStatus: this.$config.status.IDLE,
      saveStatus: this.$config.status.IDLE,
      gender: '',
      title: '',
      firstName: '',
      lastName: '',
      pronouns: '',
      dateOfBirth: '',
      countryOfBirth: '',
      street: '',
      houseNumber: '',
      postalCode: '',
      state: '',
      city: '',
      phone: '',
      image: '',
      tempImage: null,
      regionalGroup: null,
      dataEvaluation: false,
      dataThirdParty: false,
      dataProtection: false,
      imageChanged: false,
      created: null
    }
  },

  validations: {
    gender: {
      required
    },
    firstName: {
      required
    },
    lastName: {
      required
    },
    dateOfBirth: {
      required
    },
    countryOfBirth: {
      required
    },
    street: {
      required
    },
    houseNumber: {
      required,
      maxLength: maxLength(5)
    },
    postalCode: {
      maxLength: maxLength(5),
      minLength: minLength(5),
      required
    },
    state: {
      required
    },
    city: {
      required
    },
    phone: {
      required
    },
    regionalGroup: {
      required
    },
    dataEvaluation: {
      is: is(true)
    },
    dataThirdParty: {
      is: is(true)
    },
    dataProtection: {
      is: is(true)
    }
  },

  computed: {
    showImportHint () {
      if (this.loadStatus !== this.$config.status.SUCCESS) {
        return false
      }

      let joined = new Date(this.created)
      let now = new Date()
      let diff = Math.round((now.getTime() - joined.getTime()) / 60000)

      return diff < 10
    }
  },

  created () {
    this.loadStatus = this.$config.status.LOADING

    this.$api.get('user/profile/')
      .then(response => {
        this.created = response.data.created
        this.title = response.data.title
        this.gender = response.data.gender
        this.firstName = response.data.first_name
        this.lastName = response.data.last_name
        this.pronouns = response.data.pronouns
        this.dateOfBirth = response.data.birthday
        this.countryOfBirth = response.data.birthcountry
        this.street = response.data.street
        this.houseNumber = response.data.street_nr
        this.postalCode = response.data.zip
        this.state = response.data.state
        this.city = response.data.city
        this.phone = response.data.phone
        this.regionalGroup = response.data.regional_group
        this.dataEvaluation = response.data.data_evaluation
        this.dataThirdParty = response.data.data_thirdparty
        this.dataProtection = response.data.data_protection

        this.image = response.data.image
        this.tempImage = this.$config.appUrl + this.image

        this.loadStatus = this.$config.status.SUCCESS
      })
      .catch(error => {
        handleRequestError({
          error,
          fallback: 'Ihre persönlichen Daten können zurzeit leider nicht geladen werden. Bitte versuchen Sie es später erneut.'
        })

        // Redirect to dashboard only if registration is completed
        // This avoids an infinite loop between personal data and dashboard
        if (this.$store.state.user.data.completed_registration) {
          this.$router.push({ name: 'dashboard' })
        }

        this.loadStatus = this.$config.status.ERROR
      })
  },

  methods: {
    changeImage (image) {
      if (typeof image === 'undefined') {
        return
      }

      this.image = image
      this.tempImage = URL.createObjectURL(image)
      this.imageChanged = true
    },

    submit () {
      this.$v.$touch()

      if (this.$v.$invalid) {
        this.$store.commit('notifications/add', {
          text: 'Bitte füllen Sie alle Felder korrekt aus.',
          type: 'error'
        })

        return
      }

      this.saveStatus = this.$config.status.SAVING

      let data = new FormData()

      data.append('gender', this.gender)
      data.append('title', this.title)
      data.append('first_name', this.firstName)
      data.append('last_name', this.lastName)
      data.append('pronouns', this.pronouns)
      data.append('birthday', this.dateOfBirth)
      data.append('birthcountry', this.countryOfBirth)
      data.append('phone', this.phone)
      data.append('street', this.street)
      data.append('street_nr', this.houseNumber)
      data.append('zip', this.postalCode)
      data.append('state', this.state)
      data.append('city', this.city)
      data.append('regional_group', this.regionalGroup)
      data.append('data_evaluation', this.dataEvaluation ? 1 : 0)
      data.append('data_thirdparty', this.dataThirdParty ? 1 : 0)
      data.append('data_protection', this.dataProtection ? 1 : 0)

      if (this.imageChanged && this.image) {
        data.append('image', this.image)
      }

      this.$api.post('user/profile/', data)
        .then(response => {
          this.$store.commit('user/data', response.data)
          this.$store.commit('user/completedRegistration', true)
          this.$store.commit('notifications/add', {
            text: 'Ihre Benutzerinformationen wurden aktualisiert.',
            type: 'success'
          })
          this.$router.push({ name: 'dashboard' })
        })
        .catch(error => {
          handleRequestError({
            error,
            fallback: 'Ihre persönlichen Daten können zurzeit leider nicht gespeichert werden. Bitte versuchen Sie es später erneut.'
          })

          this.saveStatus = this.$config.status.ERROR
        })
    }
  }
}
</script>

<style scoped>
.input__marginless {
  margin-bottom: 0;
}
</style>
